import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatMadridDateTime } from "@/lib/datetime";
import { toast } from "sonner";
import { Building2, FileText, Download, Eye, X, RefreshCw, Pencil, UserCheck, AlertTriangle, Link2, Loader2 } from "lucide-react";

const STATUS_STYLES = {
  pending_review: { bg: "#FFF7E6", color: "#7A5A00", dot: "#E3A800" },
  approved: { bg: "var(--accent-tint)", color: "var(--accent-ink)", dot: "#6E8B3D" },
  rejected: { bg: "#FDEDE7", color: "#B4381F", dot: "#D44A28" },
};
const API = process.env.REACT_APP_BACKEND_URL;

// Campos editables por el personal CRM antes de aprobar (coinciden con B2BApplicationEdit).
const EDIT_FIELDS = [
  "company_name", "commercial_name", "tax_id", "fiscal_address", "postal_code",
  "city", "province", "country", "contact_first_name", "contact_last_name",
  "phone", "email", "role", "website", "business_activity", "notes", "language_code",
];

const Badge = ({ s, label }) => {
  const st = STATUS_STYLES[s] || { bg: "#F0F0EC", color: "#525252", dot: "#8A9098" };
  return (
    <span className="ds-pill" style={{ background: st.bg, color: st.color }}>
      <span className="dot" style={{ background: st.dot }} />
      {label || s}
    </span>
  );
};

export default function B2BAdmin() {
  const [items, setItems] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState(null); // { appId, existing }
  const [langs, setLangs] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/b2b/applications");
      setItems(r.data?.items || []);
      setStatuses(r.data?.statuses || {});
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    api.get("/languages/active").then((r) => setLangs(r.data?.languages || [])).catch(() => {});
  }, []);

  const openDetail = async (id) => {
    try { const r = await api.get(`/b2b/applications/${id}`); setSel(r.data); setEditing(false); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const changeStatus = async (id, status) => {
    setBusy(true);
    try {
      const r = await api.put(`/b2b/applications/${id}`, { status });
      const d = r.data || {};
      if (status === "approved" && d.client_id) {
        toast.success(d.link_type === "linked"
          ? `Solicitud vinculada al cliente ${d.client_code || ""}`
          : `Cliente ${d.client_code || ""} creado y vinculado`);
      } else {
        toast.success("Estado actualizado");
      }
      await load();
      await openDetail(id);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "existing_customer") {
        setDup({ appId: id, existing: detail.existing || {} });
        return;
      }
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const linkExisting = async () => {
    if (!dup) return;
    setBusy(true);
    try {
      const r = await api.post(`/b2b/applications/${dup.appId}/link-customer`, { customer_id: dup.existing.id });
      toast.success(`Solicitud vinculada al cliente ${r.data?.client_code || ""}`);
      const appId = dup.appId;
      setDup(null);
      await load();
      await openDetail(appId);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const startEdit = () => { setForm({ ...sel }); setEditing(true); };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const payload = {};
      EDIT_FIELDS.forEach((k) => { payload[k] = form[k] ?? ""; });
      await api.put(`/b2b/applications/${sel.id}/data`, payload);
      toast.success("Datos de la solicitud actualizados");
      setEditing(false);
      await load();
      await openDetail(sel.id);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const setFld = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const isApproved = sel?.status === "approved";

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between pb-4 border-b border-dashed border-[#D5D7CE] gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[.14em] text-[#3F5222] font-semibold">
            <span className="text-[#8A9098]">//</span>&nbsp;&nbsp;Clientes profesionales
          </div>
          <h1 className="font-display text-2xl font-black tracking-tight flex items-center gap-2 mt-1"><Building2 size={22} /> Solicitudes B2B</h1>
        </div>
        <button onClick={load} disabled={loading} data-testid="b2b-refresh" className="ds-btn">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Actualizar
        </button>
      </header>

      <div className="ds-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="b2b-table">
          <thead>
            <tr className="text-left font-mono uppercase tracking-wider text-[10px] text-[#525252]" style={{ background: "linear-gradient(180deg,#FDFDFB 0%,#FAFAF6 100%)" }}>
              <th className="px-3 py-2.5">Fecha solicitud</th>
              <th className="px-3 py-2.5">Empresa</th>
              <th className="px-3 py-2.5">NIF/CIF</th>
              <th className="px-3 py-2.5">Contacto</th>
              <th className="px-3 py-2.5">Teléfono</th>
              <th className="px-3 py-2.5">Email</th>
              <th className="px-3 py-2.5">Cliente CRM</th>
              <th className="px-3 py-2.5">Estado</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-[#8A9098]">No hay solicitudes B2B todavía.</td></tr>}
            {items.map((a) => (
              <tr key={a.id} onClick={() => openDetail(a.id)} data-testid={`b2b-row-${a.id}`}
                className="border-t border-[#E5E5E0] hover:bg-[#F7F7F4] cursor-pointer transition-colors">
                <td className="px-3 py-2.5 font-mono whitespace-nowrap text-[#4B5158]">{formatMadridDateTime(a.created_at)}</td>
                <td className="px-3 py-2.5 font-semibold">{a.company_name}{a.commercial_name ? <span className="text-[#8A9098] font-normal"> · {a.commercial_name}</span> : null}</td>
                <td className="px-3 py-2.5 font-mono">{a.tax_id}</td>
                <td className="px-3 py-2.5">{`${a.contact_first_name} ${a.contact_last_name}`.trim() || "—"}</td>
                <td className="px-3 py-2.5">{a.phone || "—"}</td>
                <td className="px-3 py-2.5">{a.email}</td>
                <td className="px-3 py-2.5 font-mono text-[#3F5222]">{a.client_code || "—"}</td>
                <td className="px-3 py-2.5"><Badge s={a.status} label={statuses[a.status]} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sel && (
        <div className="fixed inset-0 z-50 bg-black/45 backdrop-blur-sm flex items-start justify-center p-0 sm:p-4 overflow-y-auto" onClick={() => { setSel(null); setEditing(false); }}>
          <div className="bg-white w-full sm:max-w-2xl rounded-none sm:rounded-2xl border border-[#E5E5E0] my-0 sm:my-8 min-h-screen sm:min-h-0"
            style={{ boxShadow: "0 20px 50px rgba(15,18,16,.22)" }} onClick={(e) => e.stopPropagation()} data-testid="b2b-detail">
            <div className="px-5 py-4 border-b border-[#E5E5E0] flex items-center justify-between sticky top-0 bg-white z-10 rounded-t-2xl">
              <h2 className="font-display font-bold text-base flex items-center gap-2"><span className="ds-ic-tile"><Building2 size={15} /></span> {sel.company_name}</h2>
              <button onClick={() => { setSel(null); setEditing(false); }} className="p-2 rounded-lg hover:bg-[#F0F0EB] text-[#525252]" data-testid="b2b-detail-close" aria-label="Cerrar"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-5 text-sm">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <Badge s={sel.status} label={statuses[sel.status]} />
                <div className="flex gap-2 flex-wrap">
                  {!editing && !isApproved && (
                    <button onClick={startEdit} data-testid="b2b-edit" className="ds-btn" style={{ padding: "6px 12px", fontSize: 12 }}>
                      <Pencil size={13} /> Editar datos
                    </button>
                  )}
                  {Object.entries(statuses).map(([k, label]) => (
                    <button key={k} onClick={() => changeStatus(sel.id, k)}
                      disabled={sel.status === k || busy || editing || (isApproved && k !== "approved")}
                      data-testid={`b2b-set-${k}`}
                      className={`ds-btn ${k === "approved" ? "primary" : ""}`} style={{ padding: "6px 12px", fontSize: 12 }}>
                      {k === "approved" && <UserCheck size={13} />}{label}
                    </button>
                  ))}
                </div>
              </div>

              {isApproved && sel.client_id && (
                <div className="border border-[#D7E3C4] bg-[#F3F7EB] rounded-xl px-4 py-3" data-testid="b2b-traceability">
                  <div className="font-mono text-[10px] uppercase tracking-[.14em] text-[#3F5222] font-semibold mb-1.5 flex items-center gap-1.5"><UserCheck size={12} /> Cliente CRM vinculado</div>
                  <Row k="Cliente" v={`${sel.client_code || ""} · ${sel.client_name || ""}`.replace(/^ · | · $/g, "")} />
                  <Row k="Tipo" v={sel.client_link_type === "linked" ? "Vinculado a cliente existente" : "Cliente creado desde la solicitud"} />
                  <Row k="Aprobada por" v={sel.approved_by_name} />
                  <Row k="Fecha de aprobación" v={sel.approved_at ? formatMadridDateTime(sel.approved_at) : ""} />
                </div>
              )}

              {editing ? (
                <div className="space-y-5" data-testid="b2b-edit-form">
                  <EditSection title="Empresa">
                    <EditInput label="Razón social" k="company_name" form={form} set={setFld} />
                    <EditInput label="Nombre comercial" k="commercial_name" form={form} set={setFld} />
                    <EditInput label="NIF/CIF" k="tax_id" form={form} set={setFld} />
                    <EditInput label="Dirección fiscal" k="fiscal_address" form={form} set={setFld} />
                    <EditInput label="Código postal" k="postal_code" form={form} set={setFld} />
                    <EditInput label="Población" k="city" form={form} set={setFld} />
                    <EditInput label="Provincia" k="province" form={form} set={setFld} />
                    <EditInput label="País" k="country" form={form} set={setFld} />
                    <EditInput label="Actividad" k="business_activity" form={form} set={setFld} />
                    <EditInput label="Web" k="website" form={form} set={setFld} />
                  </EditSection>
                  <EditSection title="Contacto">
                    <EditInput label="Nombre" k="contact_first_name" form={form} set={setFld} />
                    <EditInput label="Apellidos" k="contact_last_name" form={form} set={setFld} />
                    <EditInput label="Teléfono" k="phone" form={form} set={setFld} />
                    <EditInput label="Email" k="email" form={form} set={setFld} />
                    <EditInput label="Cargo" k="role" form={form} set={setFld} />
                    <div>
                      <label className="font-mono text-[10px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">Idioma</label>
                      <select data-testid="b2b-edit-language_code" className="ds-select mt-1" value={form.language_code || ""} onChange={(e) => setFld("language_code", e.target.value)}>
                        <option value="">Por defecto</option>
                        {langs.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
                      </select>
                    </div>
                  </EditSection>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">Observaciones</label>
                    <textarea data-testid="b2b-edit-notes" className="ds-textarea mt-1" rows={2} value={form.notes || ""} onChange={(e) => setFld("notes", e.target.value)} />
                  </div>
                  <div className="flex gap-2 justify-end pt-1 border-t border-dashed border-[#D5D7CE]">
                    <button onClick={() => setEditing(false)} disabled={busy} className="ds-btn" style={{ padding: "8px 14px", fontSize: 13 }}>Cancelar</button>
                    <button onClick={saveEdit} disabled={busy} data-testid="b2b-edit-save" className="ds-btn primary" style={{ padding: "8px 14px", fontSize: 13 }}>
                      {busy ? <Loader2 size={14} className="animate-spin" /> : null} Guardar cambios
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <Section title="Empresa">
                    <Row k="Razón social" v={sel.company_name} />
                    <Row k="Nombre comercial" v={sel.commercial_name} />
                    <Row k="NIF/CIF" v={sel.tax_id} />
                    <Row k="Dirección" v={[sel.fiscal_address, sel.postal_code, sel.city, sel.province, sel.country].filter(Boolean).join(", ")} />
                    <Row k="Actividad" v={sel.business_activity} />
                    <Row k="Web" v={sel.website} />
                    <Row k="Fecha solicitud" v={formatMadridDateTime(sel.created_at)} />
                  </Section>
                  <Section title="Contacto">
                    <Row k="Nombre" v={`${sel.contact_first_name} ${sel.contact_last_name}`.trim()} />
                    <Row k="Teléfono" v={sel.phone} />
                    <Row k="Email" v={sel.email} />
                    <Row k="Cargo" v={sel.role} />
                  </Section>
                  <Section title="Documentación">
                    <Row k="Tipo de documento" v={sel.document_type_label + (sel.document_type_other ? ` — ${sel.document_type_other}` : "")} />
                    {(sel.documents || []).map((d) => (
                      <div key={d.id} className="flex items-center justify-between gap-3 py-1.5 border-t border-[#F0F0EC]" data-testid={`b2b-doc-${d.id}`}>
                        <span className="flex items-center gap-2 min-w-0"><FileText size={14} className="shrink-0 text-[#525252]" /><span className="truncate">{d.original_name}</span><span className="text-[11px] text-[#8A9098] shrink-0">{(d.size_bytes / 1024).toFixed(0)} KB</span></span>
                        <span className="flex gap-2 shrink-0">
                          <button onClick={() => window.open(`${API}/api/b2b/applications/${sel.id}/documents/${d.id}`, "_blank")} className="ds-btn" style={{ padding: "5px 10px", fontSize: 12 }}><Eye size={12} /> Ver</button>
                          <button onClick={() => window.open(`${API}/api/b2b/applications/${sel.id}/documents/${d.id}?download=1`, "_blank")} className="ds-btn" style={{ padding: "5px 10px", fontSize: 12 }}><Download size={12} /> Descargar</button>
                        </span>
                      </div>
                    ))}
                    {(sel.documents || []).length === 0 && <div className="text-[#8A9098] text-xs py-2">Sin documentos.</div>}
                  </Section>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {dup && (
        <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDup(null)}>
          <div className="bg-white w-full max-w-md rounded-2xl border border-[#E5E5E0]" style={{ boxShadow: "0 20px 50px rgba(15,18,16,.25)" }} onClick={(e) => e.stopPropagation()} data-testid="b2b-dup-modal">
            <div className="px-5 py-4 border-b border-[#E5E5E0] flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#FFF3E0] text-[#B4381F]"><AlertTriangle size={16} /></span>
              <h2 className="font-display font-bold text-base">Ya existe un cliente con estos datos</h2>
            </div>
            <div className="p-5 space-y-4 text-sm">
              <p className="text-[#4B5158]">Antes de crear un cliente nuevo, revisa si corresponde a este cliente ya registrado en el CRM:</p>
              <div className="border border-[#E5E5E0] rounded-xl px-3 py-2 bg-[#FBFBF8]">
                <Row k="Cliente" v={`${dup.existing.code || ""} · ${dup.existing.name || ""}`.replace(/^ · | · $/g, "")} />
                <Row k="NIF/CIF" v={dup.existing.tax_id || dup.existing.billing_tax_id} />
                <Row k="Email" v={dup.existing.email} />
                <Row k="Teléfono" v={dup.existing.phone} />
              </div>
              <div className="flex flex-col-reverse sm:flex-row gap-2 justify-end pt-1">
                <button onClick={() => setDup(null)} disabled={busy} data-testid="b2b-dup-cancel" className="ds-btn justify-center" style={{ padding: "9px 14px", fontSize: 13 }}>
                  Cancelar aprobación y revisar
                </button>
                <button onClick={linkExisting} disabled={busy} data-testid="b2b-dup-link" className="ds-btn primary justify-center" style={{ padding: "9px 14px", fontSize: 13 }}>
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />} Vincular con este cliente
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-[.14em] text-[#3F5222] font-semibold mb-1.5">{title}</div>
    <div className="border border-[#E5E5E0] rounded-xl px-3 py-2 bg-[#FBFBF8]">{children}</div>
  </div>
);
const Row = ({ k, v }) => (
  <div className="flex gap-3 py-0.5 text-sm"><span className="text-[#8A9098] w-36 shrink-0">{k}</span><span className="text-[#111110] break-words">{v || "—"}</span></div>
);
const EditSection = ({ title, children }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-[.14em] text-[#3F5222] font-semibold mb-2">{title}</div>
    <div className="grid sm:grid-cols-2 gap-3">{children}</div>
  </div>
);
const EditInput = ({ label, k, form, set }) => (
  <div>
    <label className="font-mono text-[10px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">{label}</label>
    <input data-testid={`b2b-edit-${k}`} className="ds-input mt-1" value={form[k] || ""} onChange={(e) => set(k, e.target.value)} />
  </div>
);
